import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { useNavigate } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { SkeletonStat, SkeletonText } from "@/components/Skeleton";
import StatCard from "@/components/StatCard";
import SideSheet from "@/components/SideSheet";
import { AttendanceTicker } from "@/components/AttendanceTicker";
import { sumLate, sumEarly, getLateSlabs, getEarlySlabs } from "@/lib/attendanceShifts";
import UserLink from "@/components/UserLink";
import {
    Users,
    Building2,
    UserCog,
    CheckCircle2,
    Clock,
    AlertTriangle,
    XCircle,
    ArrowRight,
    CalendarClock,
    Sparkles,
    BookOpen,
    Home,
    CalendarOff,
    TrendingUp,
    TrendingDown,
    GraduationCap,
    Compass,
    HelpCircle,
    ChevronDown,
    ChevronRight,
    Circle,
    Activity,
    SkipForward,
    PlayCircle,
    FileText,
    Headphones,
    Book,
    Globe,
    ExternalLink,
    Trophy,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const todayISO = () => new Date().toISOString().slice(0, 10);
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-"); return `${MONTH_NAMES[parseInt(m,10)-1]} ${y}`; };

const Stat = ({ icon: Icon, label, value, color, testid, hint, onClick }) => (
    <div
        data-testid={testid}
        onClick={onClick}
        className={`bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md transition-all ${onClick ? "cursor-pointer" : ""}`}
    >
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                <div className="mt-1 sm:mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{value}</div>
                {hint && <div className="mt-1 text-[11px] sm:text-xs text-slate-500">{hint}</div>}
            </div>
            <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
    </div>
);

const Dashboard = () => {
    const { user } = useAuth();
    const { users: allUsers } = useData();
    const [summary, setSummary] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [users, setUsers] = useState([]);
    const [learningSummary, setLearningSummary] = useState(null);
    const [attendance, setAttendance] = useState([]);
    const [months, setMonths] = useState([]);
    const [knoSummary, setKnoSummary] = useState(null);
    const [roaSummary, setRoaSummary] = useState(null);
    const [roaSessions, setRoaSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [leaderboardTop, setLeaderboardTop] = useState(null);
    const [drill, setDrill] = useState(null);  // { title, subtitle, type, items }
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [s, se, u, ls, at, mo, ks, rs, rse, lb] = await Promise.all([
                    api.get("/dashboard/summary"),
                    api.get("/sessions"),
                    api.get("/users"),
                    api.get("/learning/summary").catch(() => ({ data: null })),
                    api.get("/attendance").catch(() => ({ data: [] })),
                    api.get("/attendance/months").catch(() => ({ data: { months: [] } })),
                    api.get("/kno/summary").catch(() => ({ data: null })),
                    api.get("/roa/dashboard/summary").catch(() => ({ data: null })),
                    api.get("/roa/sessions").catch(() => ({ data: [] })),
                    api.get("/leaderboard", { params: { limit: 1 } }).catch(() => ({ data: { items: [] } })),
                ]);
                setSummary(s.data);
                setSessions(se.data);
                setUsers(u.data);
                setLearningSummary(ls.data);
                setAttendance(at.data || []);
                setMonths(mo.data?.months || []);
                setKnoSummary(ks.data);
                setRoaSummary(rs.data);
                setRoaSessions(rse.data || []);
                setLeaderboardTop((lb.data?.items && lb.data.items[0]) || null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const nameOf = (uid) => users.find((x) => x.user_id === uid)?.name
        || allUsers?.find((x) => x.user_id === uid)?.name
        || "—";

    const today = todayISO();
    // Merge PAS + ROA so the "Upcoming sessions" and "Overdue" lists are
    // truly cross-module: a manager glancing at the dashboard sees both
    // 45-day PAS touchpoints and 6-month ROA touchpoints in priority order.
    const allSessions = useMemo(() => [
        ...sessions.map((s) => ({ ...s, _module: "pas" })),
        ...roaSessions.map((s) => ({ ...s, _module: "roa" })),
    ], [sessions, roaSessions]);

    const upcomingList = allSessions
        .filter((s) => s.status === "upcoming" && s.scheduled_date >= today)
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
        .slice(0, 5);

    const overdueList = allSessions
        .filter((s) => s.status === "upcoming" && s.scheduled_date < today)
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
        .slice(0, 5);

    const recentConducted = allSessions
        .filter((s) => s.status === "conducted")
        .sort((a, b) => (b.conducted_date || "").localeCompare(a.conducted_date || ""))
        .slice(0, 5);

    const latestMonth = months[0] || null;
    const latestMonthRecords = useMemo(() => attendance.filter((r) => r.month === latestMonth), [attendance, latestMonth]);

    const attendanceAgg = useMemo(() => {
        const agg = { late: 0, lateSevere: 0, early: 0, earlySevere: 0, oof: 0, wfh: 0, total: 0, count: latestMonthRecords.length };
        for (const r of latestMonthRecords) {
            const [, , l3] = getLateSlabs(r);
            const [, , e3] = getEarlySlabs(r);
            agg.late += sumLate(r);
            agg.lateSevere += l3;
            agg.early += sumEarly(r);
            agg.earlySevere += e3;
            agg.oof += r.oof || 0;
            agg.wfh += r.wfh || 0;
            agg.total += r.total_instances || 0;
        }
        return agg;
    }, [latestMonthRecords]);

    const topFlagged = useMemo(() => {
        return [...latestMonthRecords]
            .map((r) => {
                const [, l2, l3] = getLateSlabs(r);
                const [, e2, e3] = getEarlySlabs(r);
                return { ...r, flag: l3 * 2 + e3 * 2 + l2 + e2 };
            })
            .filter((r) => r.flag > 0)
            .sort((a, b) => b.flag - a.flag)
            .slice(0, 3);
    }, [latestMonthRecords]);

    // For employee: personal attendance trend (last 6 months)
    const personalTrend = useMemo(() => {
        if (user?.role !== "employee") return null;
        const mine = attendance.filter((r) => r.user_id === user.user_id).sort((a, b) => a.month.localeCompare(b.month));
        if (mine.length === 0) return null;
        const last = mine[mine.length - 1];
        const prev = mine.length > 1 ? mine[mine.length - 2] : null;
        const delta = prev ? (last.total_instances || 0) - (prev.total_instances || 0) : 0;
        return { last, prev, delta };
    }, [attendance, user]);

    const pieData = summary
        ? [
              { name: "Conducted", value: summary.conducted, color: "#22c55e" },
              { name: "Upcoming", value: summary.upcoming - (summary.overdue || 0), color: "#3b82f6" },
              { name: "Overdue", value: summary.overdue || 0, color: "#eab308" },
              { name: "Missed", value: summary.missed, color: "#ef4444" },
          ].filter((d) => d.value > 0)
        : [];

    // Pie data for ROA mirrors PAS but with an indigo hue for "Upcoming" so
    // the eye can immediately tell PAS and ROA donuts apart on the dashboard.
    const roaPieData = roaSummary
        ? [
              { name: "Conducted", value: roaSummary.conducted, color: "#22c55e" },
              { name: "Upcoming", value: roaSummary.upcoming - (roaSummary.overdue || 0), color: "#6366f1" },
              { name: "Overdue", value: roaSummary.overdue || 0, color: "#eab308" },
              { name: "Missed", value: roaSummary.missed, color: "#ef4444" },
          ].filter((d) => d.value > 0)
        : [];

    // --- Trend computations (last 6 months) ---
    const sixMonths = useMemo(() => {
        const arr = [];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
        }
        return arr;
    }, []);

    const pasTrends = useMemo(() => {
        // Bucket sessions by month (conducted_date for conducted, scheduled_date for others)
        const buckets = { conducted: {}, upcoming: {}, overdue: {}, missed: {} };
        for (const s of sessions) {
            const dateStr = s.status === "conducted" ? (s.conducted_date || s.scheduled_date) : s.scheduled_date;
            if (!dateStr) continue;
            const ym = dateStr.slice(0, 7);
            const isOverdue = s.status === "upcoming" && s.scheduled_date < today;
            const key = s.status === "conducted" ? "conducted"
                : s.status === "missed" ? "missed"
                : isOverdue ? "overdue"
                : "upcoming";
            buckets[key][ym] = (buckets[key][ym] || 0) + 1;
        }
        const seq = (b) => sixMonths.map((m) => b[m] || 0);
        return { conducted: seq(buckets.conducted), upcoming: seq(buckets.upcoming), overdue: seq(buckets.overdue), missed: seq(buckets.missed) };
    }, [sessions, sixMonths, today]);

    // ROA trends (same shape as PAS)
    const roaTrends = useMemo(() => {
        const buckets = { conducted: {}, upcoming: {}, overdue: {}, missed: {} };
        for (const s of roaSessions) {
            const dateStr = s.status === "conducted" ? (s.conducted_date || s.scheduled_date) : s.scheduled_date;
            if (!dateStr) continue;
            const ym = dateStr.slice(0, 7);
            const isOverdue = s.status === "upcoming" && s.scheduled_date < today;
            const key = s.status === "conducted" ? "conducted"
                : s.status === "missed" ? "missed"
                : isOverdue ? "overdue"
                : "upcoming";
            buckets[key][ym] = (buckets[key][ym] || 0) + 1;
        }
        const seq = (b) => sixMonths.map((m) => b[m] || 0);
        return { conducted: seq(buckets.conducted), upcoming: seq(buckets.upcoming), overdue: seq(buckets.overdue), missed: seq(buckets.missed) };
    }, [roaSessions, sixMonths, today]);

    const deltaOf = (arr, inverted = false) => {
        if (!arr || arr.length < 2) return null;
        const last = arr[arr.length - 1];
        const prev = arr[arr.length - 2];
        const diff = last - prev;
        const pct = prev === 0 ? (last > 0 ? 100 : 0) : Math.round((diff / prev) * 100);
        return { value: diff, percent: pct, inverted };
    };

    const openDrill = (type) => {
        if (type === "conducted") setDrill({ title: "Conducted sessions", subtitle: "All PAS sessions with status = conducted", type: "sessions", items: sessions.filter((s) => s.status === "conducted").sort((a,b)=>(b.conducted_date||"").localeCompare(a.conducted_date||"")) });
        if (type === "upcoming") setDrill({ title: "Upcoming sessions", subtitle: "Scheduled and not yet due", type: "sessions", items: sessions.filter((s) => s.status === "upcoming" && s.scheduled_date >= today).sort((a,b)=>a.scheduled_date.localeCompare(b.scheduled_date)) });
        if (type === "overdue") setDrill({ title: "Overdue sessions", subtitle: "Scheduled date has passed", type: "sessions", items: sessions.filter((s) => s.status === "upcoming" && s.scheduled_date < today).sort((a,b)=>a.scheduled_date.localeCompare(b.scheduled_date)) });
        if (type === "missed") setDrill({ title: "Missed sessions", subtitle: "Marked as missed", type: "sessions", items: sessions.filter((s) => s.status === "missed") });
        if (type === "att-total") setDrill({ title: `Attendance · ${fmtMonth(latestMonth)}`, subtitle: "All records for this month", type: "attendance", items: latestMonthRecords });
        if (type === "att-late") setDrill({ title: `Late entries · ${fmtMonth(latestMonth)}`, subtitle: "Employees with one or more late entries", type: "attendance", items: latestMonthRecords.filter((r) => sumLate(r) > 0).sort((a,b) => sumLate(b) - sumLate(a)) });
        if (type === "att-early") setDrill({ title: `Early exits · ${fmtMonth(latestMonth)}`, subtitle: "Employees with one or more early exits", type: "attendance", items: latestMonthRecords.filter((r) => sumEarly(r) > 0).sort((a,b) => sumEarly(b) - sumEarly(a)) });
        if (type === "att-wfh") setDrill({ title: `WFH · ${fmtMonth(latestMonth)}`, subtitle: "Work-from-home days this month", type: "attendance", items: latestMonthRecords.filter((r) => (r.wfh||0) > 0).sort((a,b) => (b.wfh||0) - (a.wfh||0)) });
    };

    const attendanceTrends = useMemo(() => {
        // For admin/manager view: aggregate across scope by month; for employee: self only.
        const byMonth = { total: {}, late: {}, early: {}, wfh: {}, oof: {} };
        const self = user?.role === "employee";
        for (const r of attendance) {
            if (self && r.user_id !== user.user_id) continue;
            const m = r.month;
            byMonth.total[m] = (byMonth.total[m] || 0) + (r.total_instances || 0);
            byMonth.late[m] = (byMonth.late[m] || 0) + sumLate(r);
            byMonth.early[m] = (byMonth.early[m] || 0) + sumEarly(r);
            byMonth.wfh[m] = (byMonth.wfh[m] || 0) + (r.wfh || 0);
            byMonth.oof[m] = (byMonth.oof[m] || 0) + (r.oof || 0);
        }
        const seq = (b) => sixMonths.map((m) => b[m] || 0);
        return { total: seq(byMonth.total), late: seq(byMonth.late), early: seq(byMonth.early), wfh: seq(byMonth.wfh), oof: seq(byMonth.oof) };
    }, [attendance, sixMonths, user]);

    const role = user?.role;
    const roleLabel = role === "super_admin" ? "Division view" : role === "manager" ? "Team view" : "Your view";
    const headline = role === "super_admin"
        ? "Everything, everywhere, across Odyssey."
        : role === "manager"
        ? "Your team, your team's workday, one place."
        : "Your workday at a glance.";


    return (
        <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{roleLabel}</div>
                    <h1 className="font-heading text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-1">
                        Dashboard
                    </h1>
                    <p className="text-sm sm:text-base text-slate-500 mt-2">{headline}</p>
                </div>
                {(role === "super_admin" || role === "manager") && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate("/control-panel")}
                            data-testid="go-control-panel"
                            className="inline-flex items-center gap-2 h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors"
                        >
                            <Sparkles className="h-4 w-4" />
                            Open Control Panel
                        </button>
                    </div>
                )}
            </div>

            {/* Leader hero — celebrates the current #1 on the org XP
                leaderboard. Contextual: congratulates the viewer when they
                lead, motivates everyone else. Renders only when we have a
                leader (someone with XP > 0). */}
            {leaderboardTop && (
                (() => {
                    const isMe = leaderboardTop.user_id === user?.user_id;
                    const name = leaderboardTop.name || "A teammate";
                    const pts = Number(leaderboardTop.xp || 0).toLocaleString("en-IN");
                    const levelLabel = leaderboardTop.level?.label || "";
                    const streak = Number(leaderboardTop.current_streak || 0);
                    const initials = (name).split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
                    return (
                        <div
                            data-testid="dashboard-leaderboard-hero"
                            className={`relative overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 ${
                                isMe
                                    ? "bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500"
                                    : "bg-gradient-to-r from-[#2EB4AC] via-[#25908A] to-emerald-600"
                            }`}
                        >
                            {/* Decorative rings */}
                            <div className="pointer-events-none absolute -right-20 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
                            <div className="pointer-events-none absolute -left-10 -bottom-14 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
                            <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 p-5 sm:p-6">
                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                    <div className="relative flex-shrink-0">
                                        <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white/95 ring-4 ring-white/30 flex items-center justify-center overflow-hidden shadow-xl">
                                            {leaderboardTop.picture ? (
                                                <img src={leaderboardTop.picture} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <span className={`font-heading font-bold text-2xl sm:text-3xl ${isMe ? "text-orange-600" : "text-[#1F7F79]"}`}>{initials}</span>
                                            )}
                                        </div>
                                        <div className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center shadow-lg ring-2 ring-white">
                                            <Trophy className="h-4 w-4" />
                                        </div>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold text-white/80">
                                            <span className="h-1.5 w-1.5 rounded-full bg-white/90 animate-pulse" />
                                            {isMe ? "You lead the leaderboard" : "Leading the leaderboard"}
                                        </div>
                                        <div className="mt-1 font-heading text-xl sm:text-3xl font-bold text-white tracking-tight truncate">
                                            {isMe ? "You’re on fire 🔥" : name}
                                        </div>
                                        <div className="mt-2 flex items-center flex-wrap gap-2">
                                            <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold ring-1 ring-white/25">
                                                <Trophy className="h-3 w-3" /> {pts} XP
                                            </span>
                                            {levelLabel && (
                                                <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold ring-1 ring-white/25">
                                                    {levelLabel}
                                                </span>
                                            )}
                                            {streak > 0 && (
                                                <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold ring-1 ring-white/25">
                                                    🔥 {streak}-day streak
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-2 text-sm text-white/90 max-w-xl">
                                            {isMe
                                                ? "Keep shipping learnings, reactions and streaks — no one is close."
                                                : `${name.split(" ")[0]} has ${pts} XP. Complete a learning, react to a resource or keep your streak alive to close the gap.`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex sm:flex-col gap-2 sm:items-end">
                                    <button
                                        onClick={() => navigate("/activity")}
                                        data-testid="dashboard-leaderboard-cta"
                                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white text-slate-900 hover:bg-white/90 text-sm font-semibold shadow-md transition-colors"
                                    >
                                        {isMe ? "See leaderboard" : "Catch up now"}
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                    {!isMe && (
                                        <button
                                            onClick={() => navigate("/learning")}
                                            data-testid="dashboard-leaderboard-secondary"
                                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm text-sm font-semibold ring-1 ring-white/20 transition-colors"
                                        >
                                            Earn XP
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()
            )}

            {/* Cadence reminder — same ticker used on /attendance */}
            <AttendanceTicker />

            {/* Learning Hub — collapsible "Assigned to you" bar.
                Replaces the older one-liner "X resources waiting for you"
                so a single component owns the entire assigned-learning
                surface on the dashboard. */}
            {learningSummary && learningSummary.total > 0 && (
                <AssignedResourcesBar
                    summary={learningSummary}
                    onOpenResource={(r) => {
                        if (r.url) window.open(r.url, "_blank", "noopener");
                        navigate(`/learning?collection=${r.collection_id}`);
                    }}
                    onOpenHub={() => navigate("/learning")}
                />
            )}

            {/* PAS + ROA primary stats — alongside so the eye can compare the
                45-day cadence (left) with the 6-month cadence (right) in one glance. */}
            <Section title="Sessions overview" subtitle="PAS · ROA — at a glance" icon={CalendarClock}>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                    {/* PAS column */}
                    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-lg bg-teal-50 text-[#25908A] border border-teal-100 flex items-center justify-center">
                                    <CalendarClock className="h-4 w-4" />
                                </div>
                                <div>
                                    <div className="font-heading font-semibold text-slate-900">PAS Sessions</div>
                                    <div className="text-[11px] text-slate-500">Performance Alignment · 45-day cadence</div>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate("/calendar?type=pas")}
                                className="text-xs text-[#25908A] hover:text-[#2EB4AC] font-medium inline-flex items-center gap-1"
                                data-testid="open-pas-link"
                            >
                                Open <ArrowRight className="h-3 w-3" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {loading ? (
                                <><SkeletonStat /><SkeletonStat /><SkeletonStat /><SkeletonStat /></>
                            ) : (
                                <>
                                    <StatCard icon={CheckCircle2} label="Conducted" value={summary?.conducted ?? 0} color="bg-green-100 text-green-700" testid="stat-conducted" trend={pasTrends.conducted} delta={deltaOf(pasTrends.conducted)} hint="last 6 months" onClick={() => openDrill("conducted")} />
                                    <StatCard icon={Clock} label="Upcoming" value={summary?.upcoming ?? 0} color="bg-blue-100 text-blue-700" testid="stat-upcoming" trend={pasTrends.upcoming} delta={deltaOf(pasTrends.upcoming)} hint="last 6 months" onClick={() => openDrill("upcoming")} />
                                    <StatCard icon={AlertTriangle} label="Overdue" value={summary?.overdue ?? 0} color="bg-yellow-100 text-yellow-700" testid="stat-overdue" trend={pasTrends.overdue} delta={deltaOf(pasTrends.overdue, true)} hint="last 6 months" onClick={() => openDrill("overdue")} />
                                    <StatCard icon={XCircle} label="Missed" value={summary?.missed ?? 0} color="bg-red-100 text-red-700" testid="stat-missed" trend={pasTrends.missed} delta={deltaOf(pasTrends.missed, true)} hint="last 6 months" onClick={() => openDrill("missed")} />
                                </>
                            )}
                        </div>
                    </div>

                    {/* ROA column */}
                    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center justify-center">
                                    <Compass className="h-4 w-4" />
                                </div>
                                <div>
                                    <div className="font-heading font-semibold text-slate-900">ROA Sessions</div>
                                    <div className="text-[11px] text-slate-500">Roadmap of Association · 6-month cadence</div>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate("/calendar?type=roa")}
                                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1"
                                data-testid="open-roa-link"
                            >
                                Open <ArrowRight className="h-3 w-3" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {loading ? (
                                <><SkeletonStat /><SkeletonStat /><SkeletonStat /><SkeletonStat /></>
                            ) : (
                                <>
                                    <StatCard icon={CheckCircle2} label="Conducted" value={roaSummary?.conducted ?? 0} color="bg-green-100 text-green-700" testid="roa-stat-conducted" trend={roaTrends.conducted} delta={deltaOf(roaTrends.conducted)} hint="last 6 months" onClick={() => navigate("/calendar?type=roa")} />
                                    <StatCard icon={Clock} label="Upcoming" value={roaSummary?.upcoming ?? 0} color="bg-indigo-100 text-indigo-700" testid="roa-stat-upcoming" trend={roaTrends.upcoming} delta={deltaOf(roaTrends.upcoming)} hint="last 6 months" onClick={() => navigate("/calendar?type=roa")} />
                                    <StatCard icon={AlertTriangle} label="Overdue" value={roaTrends.overdue[roaTrends.overdue.length - 1] || 0} color="bg-yellow-100 text-yellow-700" testid="roa-stat-overdue" trend={roaTrends.overdue} delta={deltaOf(roaTrends.overdue, true)} hint="last 6 months" onClick={() => navigate("/calendar?type=roa")} />
                                    <StatCard icon={XCircle} label="Missed" value={roaSummary?.missed ?? 0} color="bg-red-100 text-red-700" testid="roa-stat-missed" trend={roaTrends.missed} delta={deltaOf(roaTrends.missed, true)} hint="last 6 months" onClick={() => navigate("/calendar?type=roa")} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Section>

            {/* Donut split (PAS + ROA) + merged upcoming list */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 lg:col-span-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-heading text-xl font-semibold text-slate-900">Session Health</h3>
                            <p className="text-sm text-slate-500">PAS &amp; ROA distribution this year</p>
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <Donut data={pieData} label="PAS" emptyHint="No PAS yet" />
                        <Donut data={roaPieData} label="ROA" emptyHint="No ROA yet" />
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 lg:col-span-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-heading text-xl font-semibold text-slate-900">Upcoming sessions</h3>
                            <p className="text-sm text-slate-500">PAS &amp; ROA · next 5 scheduled</p>
                        </div>
                        <button onClick={() => navigate("/calendar")} className="text-sm text-[#25908A] hover:text-[#2EB4AC] font-medium inline-flex items-center gap-1">
                            View calendar <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="mt-4 divide-y divide-slate-100">
                        {upcomingList.length === 0 ? (
                            <div className="py-8 text-center text-slate-500 text-sm">No upcoming sessions</div>
                        ) : (
                            upcomingList.map((s) => {
                                const isRoa = s._module === "roa";
                                return (
                                    <div key={`${s._module}-${s.id}`} className="flex items-center justify-between py-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center border ${
                                                isRoa
                                                    ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                                                    : "bg-blue-50 text-blue-700 border-blue-100"
                                            }`}>
                                                {isRoa ? <Compass className="h-4 w-4" /> : <CalendarClock className="h-4 w-4" />}
                                            </div>
                                            <div>
                                                <div className="font-medium text-slate-900 flex items-center gap-2">
                                                    <UserLink userId={s.employee_id} name={nameOf(s.employee_id)} testid={`dashboard-upcoming-name-${s.id}`} />
                                                    <span className={`text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded-md ${
                                                        isRoa ? "bg-indigo-50 text-indigo-700 border border-indigo-100" : "bg-teal-50 text-[#25908A] border border-teal-100"
                                                    }`}>
                                                        {isRoa ? "ROA" : "PAS"}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-slate-500">Cycle #{s.cycle_number} · {s.scheduled_date}</div>
                                            </div>
                                        </div>
                                        <StatusBadge status="upcoming" />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Overdue highlight */}
            {overdueList.length > 0 && (
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            <h3 className="font-heading text-lg font-semibold text-slate-900">Needs attention — Overdue sessions</h3>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {overdueList.map((s) => {
                            const isRoa = s._module === "roa";
                            return (
                                <div key={`${s._module}-${s.id}`} className="bg-white border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-slate-900 flex items-center gap-2">
                                            <UserLink userId={s.employee_id} name={nameOf(s.employee_id)} testid={`dashboard-overdue-name-${s.id}`} />
                                            <span className={`text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded-md ${
                                                isRoa ? "bg-indigo-50 text-indigo-700 border border-indigo-100" : "bg-teal-50 text-[#25908A] border border-teal-100"
                                            }`}>
                                                {isRoa ? "ROA" : "PAS"}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500">Scheduled {s.scheduled_date} · Cycle #{s.cycle_number}</div>
                                    </div>
                                    <StatusBadge status="overdue" />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* "Next ROA" highlight strip — kept after the upcoming list so the
                very next 6-month touchpoint is visually distinct from the 5-day cards. */}
            {roaSummary?.next && (
                <div className="border-2 border-dashed border-indigo-300 bg-indigo-50/40 rounded-lg p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                            <Compass className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider text-indigo-600 font-semibold">Next ROA</div>
                            <div className="font-heading text-base font-semibold text-slate-900 truncate">
                                <UserLink userId={roaSummary.next.employee_id} name={nameOf(roaSummary.next.employee_id)} testid="dashboard-next-roa-name" /> · Cycle #{roaSummary.next.cycle_number}
                            </div>
                            <div className="text-xs text-slate-500">Scheduled {roaSummary.next.scheduled_date}</div>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/calendar?type=roa")}
                        className="hidden sm:inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        data-testid="next-roa-cta"
                    >
                        Open <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* ATTENDANCE SECTION */}
            {attendance.length > 0 && (
                <Section
                    title="Attendance"
                    subtitle={latestMonth ? `Latest: ${fmtMonth(latestMonth)} · ${attendanceAgg.count} ${attendanceAgg.count === 1 ? "person" : "people"} reported` : "Monthly rollup"}
                    icon={Clock}
                    action={
                        <button onClick={() => navigate("/attendance")} className="text-sm text-[#25908A] hover:text-[#2EB4AC] font-medium inline-flex items-center gap-1">
                            Open attendance <ArrowRight className="h-4 w-4" />
                        </button>
                    }
                >
                    {role === "employee" && personalTrend ? (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <StatCard
                                icon={Clock}
                                label={`Total · ${fmtMonth(personalTrend.last.month)}`}
                                value={personalTrend.last.total_instances || 0}
                                color="bg-slate-100 text-slate-700"
                                testid="emp-attendance-total"
                                trend={attendanceTrends.total}
                                delta={deltaOf(attendanceTrends.total, true)}
                                hint="last 6 months"
                                onClick={() => openDrill("att-total")}
                            />
                            <StatCard icon={CalendarOff} label="OFF / OOF this month" value={personalTrend.last.oof || 0} color="bg-amber-100 text-amber-700" testid="emp-attendance-oof" trend={attendanceTrends.oof} />
                            <StatCard icon={Home} label="WFH this month" value={personalTrend.last.wfh || 0} color="bg-blue-100 text-blue-700" testid="emp-attendance-wfh" trend={attendanceTrends.wfh} />
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                                <StatCard icon={Clock} label="Total instances" value={attendanceAgg.total} color="bg-slate-100 text-slate-700" testid="att-total" hint={`${attendanceAgg.count} people`} trend={attendanceTrends.total} delta={deltaOf(attendanceTrends.total, true)} onClick={() => openDrill("att-total")} />
                                <StatCard icon={TrendingUp} label="Late entries" value={attendanceAgg.late} color="bg-yellow-100 text-yellow-700" testid="att-late" hint={`${attendanceAgg.lateSevere} beyond grace`} trend={attendanceTrends.late} delta={deltaOf(attendanceTrends.late, true)} onClick={() => openDrill("att-late")} />
                                <StatCard icon={TrendingDown} label="Early exits" value={attendanceAgg.early} color="bg-red-100 text-red-700" testid="att-early" hint={`${attendanceAgg.earlySevere} very early`} trend={attendanceTrends.early} delta={deltaOf(attendanceTrends.early, true)} onClick={() => openDrill("att-early")} />
                                <StatCard icon={Home} label="WFH days" value={attendanceAgg.wfh} color="bg-blue-100 text-blue-700" testid="att-wfh" hint={`${attendanceAgg.oof} OFF taken`} trend={attendanceTrends.wfh} delta={deltaOf(attendanceTrends.wfh)} onClick={() => openDrill("att-wfh")} />
                            </div>

                            {topFlagged.length > 0 && (
                                <div className="mt-6 border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                        <h3 className="font-heading text-base font-semibold text-slate-900">
                                            Needs a conversation — {fmtMonth(latestMonth)}
                                        </h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {topFlagged.map((r) => {
                                            const [, , l3] = getLateSlabs(r);
                                            const [, , e3] = getEarlySlabs(r);
                                            const isFirm = String(r.shift || "").toLowerCase() === "firmware";
                                            const lateLabel = isFirm ? "After 09:30" : "After 10:30";
                                            const earlyLabel = isFirm ? "Before 17:00" : "Before 18:00";
                                            return (
                                            <div key={r.id} className="bg-white border border-yellow-200 rounded-lg p-3">
                                                <div className="font-medium text-slate-900">{r.employee_name}</div>
                                                <div className="text-xs text-slate-500">{r.department_name || "—"}</div>
                                                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                                                    {l3 > 0 && (
                                                        <span className="px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-800 border border-yellow-200">{lateLabel} · {l3}</span>
                                                    )}
                                                    {e3 > 0 && (
                                                        <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-800 border border-red-200">{earlyLabel} · {e3}</span>
                                                    )}
                                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">Total {r.total_instances || 0}</span>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Section>
            )}

            {/* LEARNING SECTION — assigned resources are now surfaced
                via the AssignedResourcesBar near the top of the dashboard
                (replaces the old "X resources waiting for you" banner). */}

            {/* KNO SECTION */}
            {knoSummary && (knoSummary.month_total > 0 || knoSummary.last_session_total > 0) && (
                <Section
                    title="KNO Attendance"
                    subtitle="Weekly knowledge session (Wednesdays)"
                    icon={GraduationCap}
                    action={
                        <button onClick={() => navigate("/kno")} className="text-sm text-[#25908A] hover:text-[#2EB4AC] font-medium inline-flex items-center gap-1">
                            Open KNO <ArrowRight className="h-4 w-4" />
                        </button>
                    }
                >
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        <StatCard icon={GraduationCap} label="Attendance rate" value={knoSummary.month_rate != null ? `${knoSummary.month_rate}%` : "—"} color="bg-teal-50 text-[#25908A]" testid="kno-rate" hint="this month" onClick={() => navigate("/kno")} />
                        <StatCard icon={CheckCircle2} label="Present" value={knoSummary.month_present} color="bg-green-100 text-green-700" testid="kno-present" hint="this month" onClick={() => navigate("/kno")} />
                        <StatCard icon={XCircle} label="Absent" value={knoSummary.month_absent} color="bg-red-100 text-red-700" testid="kno-absent" hint="this month" onClick={() => navigate("/kno")} />
                        <StatCard icon={Clock} label={knoSummary.last_session_date ? `Last · ${knoSummary.last_session_date.slice(5)}` : "Last session"} value={knoSummary.last_session_rate != null ? `${knoSummary.last_session_rate}%` : "—"} color="bg-slate-100 text-slate-700" testid="kno-last" hint={knoSummary.last_session_total ? `${knoSummary.last_session_present}/${knoSummary.last_session_total} present` : "no session yet"} onClick={() => navigate("/kno")} />
                    </div>
                </Section>
            )}

            {/* Organization stats row — Super Admin only */}
            {role === "super_admin" && (
                <Section title="Organization" subtitle="People & structure" icon={Users}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <Stat icon={Users} label="Employees" value={summary?.total_employees ?? 0} color="bg-teal-50 text-[#25908A]" testid="stat-employees" onClick={() => navigate("/employees")} />
                        <Stat icon={Building2} label="Departments" value={summary?.total_departments ?? 0} color="bg-purple-100 text-purple-700" testid="stat-departments" onClick={() => navigate("/departments")} />
                        <Stat icon={UserCog} label="Managers" value={summary?.total_managers ?? 0} color="bg-blue-100 text-blue-700" testid="stat-managers" onClick={() => navigate("/managers")} />
                    </div>
                </Section>
            )}

            {/* Recent conducted */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <h3 className="font-heading text-xl font-semibold text-slate-900">Recently conducted</h3>
                <p className="text-sm text-slate-500">Latest completed PAS sessions</p>
                <div className="mt-4 divide-y divide-slate-100">
                    {recentConducted.length === 0 ? (
                        <div className="py-8 text-center text-slate-500 text-sm">No sessions conducted yet</div>
                    ) : (
                        recentConducted.map((s) => (
                            <div key={s.id} className="flex items-center justify-between py-3">
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-lg bg-green-50 text-green-700 border border-green-100 flex items-center justify-center">
                                        <CheckCircle2 className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-slate-900">{nameOf(s.employee_id)}</div>
                                        <div className="text-xs text-slate-500">Conducted {s.conducted_date} · Cycle #{s.cycle_number}</div>
                                    </div>
                                </div>
                                <StatusBadge status="conducted" />
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Drill-down sheet */}
            <SideSheet
                open={!!drill}
                onClose={() => setDrill(null)}
                title={drill?.title}
                subtitle={drill?.subtitle}
                testid="dashboard-drill-sheet"
                width={520}
            >
                <DrillContent drill={drill} nameOf={nameOf} onNavigate={(p) => { setDrill(null); navigate(p); }} />
            </SideSheet>

        </div>
    );
};

const DrillContent = ({ drill, nameOf, onNavigate }) => {
    if (!drill) return null;
    if (!drill.items || drill.items.length === 0) {
        return (
            <div className="p-10 text-center">
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg py-10 px-6">
                    <p className="font-heading font-semibold text-slate-900">Nothing to show</p>
                    <p className="text-sm text-slate-500 mt-1">No matching records in scope.</p>
                </div>
            </div>
        );
    }
    if (drill.type === "sessions") {
        return (
            <div className="divide-y divide-slate-100">
                {drill.items.map((s) => (
                    <button
                        key={s.id}
                        onClick={() => onNavigate(`/employees/${s.employee_id}`)}
                        className="w-full text-left px-6 py-4 hover:bg-slate-50/60 transition-colors"
                        data-testid={`drill-session-${s.id}`}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-medium text-slate-900 truncate">{nameOf(s.employee_id)}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Cycle #{s.cycle_number} · {s.status === "conducted" ? `Conducted ${s.conducted_date || ""}` : `Scheduled ${s.scheduled_date}`}
                                </div>
                            </div>
                            <StatusBadge status={s.status === "upcoming" && s.scheduled_date < new Date().toISOString().slice(0,10) ? "overdue" : s.status} />
                        </div>
                    </button>
                ))}
            </div>
        );
    }
    // attendance
    return (
        <div className="divide-y divide-slate-100">
            {drill.items.map((r) => {
                const late = sumLate(r);
                const early = sumEarly(r);
                return (
                    <button
                        key={r.id || `${r.user_id}-${r.month}`}
                        onClick={() => onNavigate(`/employees/${r.user_id}`)}
                        className="w-full text-left px-6 py-4 hover:bg-slate-50/60 transition-colors"
                        data-testid={`drill-att-${r.id}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-medium text-slate-900 truncate">{r.employee_name}</div>
                                <div className="text-xs text-slate-500 mt-0.5 truncate">{r.department_name || "—"}</div>
                            </div>
                            <div className="flex gap-1.5 flex-wrap justify-end text-[10px]">
                                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">Total {r.total_instances||0}</span>
                                {late > 0 && <span className="px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-800 border border-yellow-200 font-semibold">Late {late}</span>}
                                {early > 0 && <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-800 border border-red-200 font-semibold">Early {early}</span>}
                                {(r.wfh||0) > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200 font-semibold">WFH {r.wfh}</span>}
                                {(r.oof||0) > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-semibold">OFF {r.oof}</span>}
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

const Section = ({ title, subtitle, icon: Icon, action, children }) => (
    <div>
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
                    {Icon && <Icon className="h-5 w-5 text-[#2EB4AC]" />}
                    {title}
                </h2>
                {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
            {action}
        </div>
        {children}
    </div>
);

// Compact donut used to render PAS and ROA distributions side-by-side. Kept
// local because the dashboard is the only page that needs this tight layout —
// avoids spinning up another shared component for now.
const Donut = ({ data, label, emptyHint }) => (
    <div className="flex flex-col items-center" data-testid={`donut-${label.toLowerCase()}`}>
        <div className="h-44 w-full">
            {(!data || data.length === 0) ? (
                <div className="h-full flex items-center justify-center text-[11px] text-slate-500 text-center px-2 border-2 border-dashed border-slate-200 bg-slate-50/40 rounded-lg">
                    {emptyHint || "No data yet"}
                </div>
            ) : (
                <ResponsiveContainer>
                    <PieChart>
                        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={32} outerRadius={62} paddingAngle={2}>
                            {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </div>
        <div className="mt-1 text-[11px] uppercase tracking-wider font-semibold text-slate-700">{label}</div>
    </div>
);

export default Dashboard;

// ============= AssignedResourcesBar =============
// Collapsible bar that summarises the user's assigned learning resources
// at-a-glance, and on expand reveals every resource as a clickable row so
// the user can jump straight to the specific item or its parent collection
// inside the Learning Hub.

const STATUS_META = {
    not_started: { Icon: Circle,        cls: "text-slate-500 bg-slate-100 border-slate-200",   label: "Not started" },
    in_progress: { Icon: Activity,      cls: "text-blue-700 bg-blue-50 border-blue-200",       label: "In progress" },
    completed:   { Icon: CheckCircle2,  cls: "text-green-700 bg-green-50 border-green-200",    label: "Completed"  },
    skipped:     { Icon: SkipForward,   cls: "text-purple-700 bg-purple-50 border-purple-200", label: "Skipped"    },
};
const TYPE_ICON_MAP = {
    video: PlayCircle, course: GraduationCap, article: FileText, doc: FileText,
    podcast: Headphones, book: Book, other: Globe, note: FileText,
};

const AssignedResourcesBar = ({ summary, onOpenResource, onOpenHub }) => {
    const [open, setOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const resources = summary?.resources || [];
    const pct = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;
    const filtered = useMemo(() => {
        if (statusFilter === "all") return resources;
        return resources.filter((r) => (r.my_status || "not_started") === statusFilter);
    }, [resources, statusFilter]);

    const chip = (key, label, count, cls) => (
        <button
            key={key}
            type="button"
            onClick={(e) => { e.stopPropagation(); setStatusFilter(key); if (!open) setOpen(true); }}
            data-testid={`lh-bar-filter-${key}`}
            className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-semibold border transition-colors ${
                statusFilter === key ? "ring-2 ring-[#2EB4AC]/40 " : ""
            }${cls}`}
        >
            <span className="tabular-nums">{count}</span> {label}
        </button>
    );

    return (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="lh-assigned-bar">
            {/* Header bar */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="lh-bar-toggle"
                className="w-full px-5 py-4 flex items-center gap-4 flex-wrap hover:bg-slate-50 transition-colors text-left"
            >
                <div className="h-11 w-11 rounded-xl bg-teal-50 text-[#25908A] flex items-center justify-center shrink-0">
                    <BookOpen className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#25908A] font-semibold">Assigned to you</div>
                    <div className="font-heading text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                        <span className="tabular-nums" data-testid="lh-bar-total">{summary.total}</span>
                        <span>{summary.total === 1 ? "resource" : "resources"}</span>
                        <span className="text-slate-300">·</span>
                        <span className="text-sm font-medium text-slate-500">
                            across {summary.assigned_collections} {summary.assigned_collections === 1 ? "collection" : "collections"}
                        </span>
                    </div>
                    {/* Slim progress bar */}
                    <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#2EB4AC] to-[#25908A] transition-[width] duration-500"
                            style={{ width: `${pct}%` }}
                            data-testid="lh-bar-progress"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {chip("all", "All", summary.total, "bg-slate-50 text-slate-700 border-slate-200")}
                    {chip("not_started", "Not started", summary.not_started, STATUS_META.not_started.cls)}
                    {chip("in_progress", "In progress", summary.in_progress, STATUS_META.in_progress.cls)}
                    {chip("completed", "Completed", summary.completed, STATUS_META.completed.cls)}
                </div>
                <span className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 inline-flex items-center justify-center shrink-0">
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
            </button>

            {/* Expanded list */}
            {open && (
                <div className="border-t border-slate-200 bg-slate-50/30" data-testid="lh-bar-list">
                    {filtered.length === 0 ? (
                        <div className="px-5 py-10 text-center text-sm text-slate-500">
                            No resources in this view.{" "}
                            <button type="button" onClick={() => setStatusFilter("all")} className="text-[#25908A] font-medium hover:underline">Show all</button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-200 max-h-[420px] overflow-y-auto scrollbar-thin">
                            {filtered.map((r) => {
                                const TypeIcon = TYPE_ICON_MAP[r.type] || Globe;
                                const meta = STATUS_META[r.my_status] || STATUS_META.not_started;
                                const StatusIcon = meta.Icon;
                                return (
                                    <li key={r.id}>
                                        <button
                                            type="button"
                                            onClick={() => onOpenResource(r)}
                                            data-testid={`lh-bar-row-${r.id}`}
                                            className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white transition-colors text-left group"
                                        >
                                            <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 text-[#25908A] flex items-center justify-center shrink-0 group-hover:border-[#2EB4AC] transition-colors">
                                                <TypeIcon className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm text-slate-900 truncate group-hover:text-[#25908A] transition-colors">{r.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {r.collection_name || "—"}
                                                </div>
                                            </div>
                                            <span className={`hidden sm:inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-semibold border ${meta.cls}`}>
                                                <StatusIcon className="h-3 w-3" /> {meta.label}
                                            </span>
                                            {r.url && (
                                                <ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#2EB4AC] transition-colors shrink-0" />
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <div className="px-5 py-3 border-t border-slate-200 bg-white flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs text-slate-500">
                            Showing <span className="font-semibold text-slate-700 tabular-nums">{filtered.length}</span> of <span className="font-semibold text-slate-700 tabular-nums">{summary.total}</span>
                        </div>
                        <button
                            type="button"
                            onClick={onOpenHub}
                            data-testid="lh-bar-open-hub"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#25908A] hover:text-[#2EB4AC]"
                        >
                            Open Learning Hub <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
